import { useState } from 'react';
import { Shield, Users, Key, Database, Save } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '@/hooks/useI18n';

export default function AdminSettings() {
  const { profile } = useAuth();
  const { toast } = useToast();
  const { t } = useI18n();
  const [promoteEmail, setPromoteEmail] = useState('');
  const [promoting, setPromoting] = useState(false);
  const [demoting, setDemoting] = useState(false);
  const [demoteEmail, setDemoteEmail] = useState('');

  const promoteAdmin = async () => {
    if (!promoteEmail) return;
    setPromoting(true);
    const { data: found } = await supabase.from('profiles').select('id, first_name, last_name').eq('email', promoteEmail).maybeSingle();
    if (!found) {
      toast({ title: t('admin_error'), description: t('admin_account_not_found'), variant: 'destructive' });
    } else {
      const { error } = await supabase.from('profiles').update({ is_admin: true }).eq('id', found.id);
      if (!error) {
        toast({ title: t('admin_success'), description: t('admin_rights_granted') });
        setPromoteEmail('');
      }
    }
    setPromoting(false);
  };

  const revokeAdmin = async () => {
    if (!demoteEmail) return;
    if (demoteEmail === profile?.email) {
      toast({ title: t('admin_error'), description: t('admin_cannot_revoke_self'), variant: 'destructive' });
      return;
    }
    setDemoting(true);
    const { data: found } = await supabase.from('profiles').select('id, first_name, last_name').eq('email', demoteEmail).maybeSingle();
    if (!found) {
      toast({ title: t('admin_error'), description: t('admin_account_not_found'), variant: 'destructive' });
    } else {
      await supabase.from('profiles').update({ is_admin: false }).eq('id', found.id);
      toast({ title: t('admin_success'), description: t('admin_rights_revoked') });
      setDemoteEmail('');
    }
    setDemoting(false);
  };

  const SYSTEM_SETTINGS = [
    { id: 'maintenance', label: t('admin_maintenance_mode'), desc: t('admin_maintenance_desc'), defaultVal: false },
    { id: 'kyc_auto', label: t('admin_auto_kyc'), desc: t('admin_auto_kyc_desc'), defaultVal: false },
    { id: 'notifications_global', label: t('admin_push_notifications'), desc: t('admin_push_notifications_desc'), defaultVal: true },
    { id: 'audit_log', label: t('admin_audit_log'), desc: t('admin_audit_log_desc'), defaultVal: true },
  ];

  return (
    <AdminLayout title={t('admin_settings_title')}>
      <div className="max-w-3xl mx-auto space-y-6">
        <Card className="border-red-900/30" style={{ background: 'linear-gradient(135deg, oklch(0.10 0.04 15) 0%, oklch(0.09 0.03 250) 100%)' }}>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-14 h-14 bg-red-500/15 rounded-2xl flex items-center justify-center border border-red-500/20">
              <Shield className="w-7 h-7 text-red-400" />
            </div>
            <div>
              <p className="font-bold text-foreground">{t('admin_logged_in_as_admin')}</p>
              <p className="text-sm text-muted-foreground">{profile?.email}</p>
              <p className="text-xs text-red-400 mt-1">{t('admin_actions_recorded')}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Key className="w-4 h-4 text-accent" /> {t('admin_grant_rights')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('admin_grant_rights_desc')}</p>
            <div className="flex gap-3">
              <Input
                type="email"
                placeholder={t('admin_email_placeholder')}
                value={promoteEmail}
                onChange={(e) => setPromoteEmail(e.target.value)}
                className="flex-1 h-11"
              />
              <Button onClick={promoteAdmin} disabled={promoting || !promoteEmail} className="bg-accent text-accent-foreground hover:bg-accent/90">
                {promoting ? t('admin_loading') : t('admin_grant_button')}
              </Button>
            </div>
            <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
              <Shield className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
              <p className="text-xs text-yellow-400">{t('admin_grant_warning')}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-destructive" /> {t('admin_revoke_rights')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-3">
              <Input
                type="email"
                placeholder={t('admin_revoke_email_placeholder')}
                value={demoteEmail}
                onChange={(e) => setDemoteEmail(e.target.value)}
                className="flex-1 h-11"
              />
              <Button
                onClick={revokeAdmin}
                disabled={demoting || !demoteEmail}
                variant="outline"
                className="border-destructive/30 text-destructive hover:bg-destructive/10"
              >
                {demoting ? t('admin_loading') : t('admin_revoke_button')}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Database className="w-4 h-4 text-blue-400" /> {t('admin_system_settings')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {SYSTEM_SETTINGS.map((s) => (
              <div key={s.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
                <div>
                  <Label htmlFor={s.id} className="text-sm font-medium cursor-pointer">{s.label}</Label>
                  <p className="text-xs text-muted-foreground">{s.desc}</p>
                </div>
                <Switch id={s.id} defaultChecked={s.defaultVal} />
              </div>
            ))}
            <Button className="w-full gap-2" variant="outline">
              <Save className="w-4 h-4" /> {t('admin_save_settings')}
            </Button>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}